import {
  signUp,
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
  updatePassword,
} from "aws-amplify/auth";

const register = async ({ username, email, phoneNumber, password }) => {
  return await signUp({
    username,
    password,
    options: {
      userAttributes: {
        email,
        phone_number: phoneNumber,
      },
      autoSignIn: { enabled: true },
    },
  });
};

const login = async (username, password) => {
  if (!username || !password) throw new Error("Username and password required");

  try {
    await signOut();
  } catch (err) {
    if (!err.message?.includes("not signed in")) {
      console.warn("Pre-login sign out failed:", err);
    }
  }

  return await signIn({ username, password });
};

const logout = async () => {
  try {
    const result = await signOut({ global: false });
    return result;
  } catch (error) {
    console.error("Logout failed:", error);
    return { success: false, error };
  }
};

const getCurrentUserInfo = async () => {
  try {
    const user = await getCurrentUser();
    if (!user) return null;

    let session, attributes;

    try {
      session = await fetchAuthSession();
    } catch (sessionErr) {
      if (isAuthError(sessionErr)) return null;
      throw sessionErr;
    }

    try {
      attributes = await fetchUserAttributes();
    } catch (attributesErr) {
      if (isAuthError(attributesErr)) return null;
      throw attributesErr;
    }

    const tokens = session.tokens;
    if (!tokens || !tokens.idToken) return null;

    const idToken = tokens.idToken.payload;

    return {
      ...user,
      ...attributes,
      attributes,
      isAdmin: idToken?.["cognito:groups"]?.includes("Admins") ?? false,
      isConfirmed:
        attributes?.email_verified === "true" ||
        attributes?.email_verified === true,
    };
  } catch (err) {
    if (isAuthError(err)) return null;
    console.warn("getCurrentUserInfo failed:", err);
    return null;
  }
};

const updateAttributes = async (attributesToUpdate) => {
  try {
    await updateUserAttributes({ userAttributes: attributesToUpdate });
  } catch (error) {
    console.error("Update attributes failed:", error);
    throw error;
  }
};

const changePassword = async (oldPassword, newPassword) => {
  try {
    await updatePassword({ oldPassword, newPassword });
  } catch (error) {
    console.error("Change password failed:", error);
    throw error;
  }
};

const isAuthError = (err) => {
  return (
    err.name === "UserUnAuthenticatedException" ||
    err.name === "NotAuthorizedException" ||
    err.message?.includes("not signed in") ||
    err.message?.includes("No current user") ||
    err.message?.includes("Unauthenticated access") ||
    err.message?.includes("User needs to be authenticated") ||
    err.message?.includes("Access Token has been revoked") ||
    err.message?.includes("400") ||
    (err.response && err.response.status === 400)
  );
};

// [F09] Removed the getCurrentUser() pre-check — fetchAuthSession() alone
// tells us everything we need. If there's no active session it throws an
// auth error which isAuthError() catches and returns null for. This saves
// one Cognito round-trip on every API call.
const getCurrentUserToken = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (err) {
    if (isAuthError(err)) return null;
    console.warn("getCurrentUserToken failed:", err);
    return null;
  }
};

const authService = {
  register,
  login,
  logout,
  getCurrentUser: getCurrentUserInfo,
  getCurrentUserToken,
  updateAttributes,
  changePassword,
};

export default authService;
